import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Users, Target, Briefcase, Ticket, Settings, ArrowLeft, Plus, Edit2, Trash2, Search, Building, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabsRoot, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import UserManagementModal from '@/components/modals/UserManagementModal';
import DeleteConfirmationModal from '@/components/modals/DeleteConfirmationModal';
import CreateEditModal from '@/components/modals/CreateEditModal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api';

const AdminDashboard = () => {
    // --- Empresas State/Handlers ---
    const [empresas, setEmpresas] = useState([]);
    const [isEmpresaModalOpen, setIsEmpresaModalOpen] = useState(false);
    const [selectedEmpresa, setSelectedEmpresa] = useState(null);

    // --- Metas State/Handlers ---
    const [metas, setMetas] = useState([]);
    const [isMetaModalOpen, setIsMetaModalOpen] = useState(false);
    const [selectedMeta, setSelectedMeta] = useState(null);

    // --- Serviços State/Handlers ---
    const [servicos, setServicos] = useState([]);
    const [isServicoModalOpen, setIsServicoModalOpen] = useState(false);
    const [selectedServico, setSelectedServico] = useState(null);

    // --- Implementações State/Handlers ---
    const [atendimentos, setAtendimentos] = useState([]);
    const [isAtendimentoModalOpen, setIsAtendimentoModalOpen] = useState(false);
    const [selectedAtendimento, setSelectedAtendimento] = useState(null);

    // --- Implementações State/Handlers ---
    const [implementacoes, setImplementacoes] = useState([]);
    const [isImplementacaoModalOpen, setIsImplementacaoModalOpen] = useState(false);
    const [selectedImplementacao, setSelectedImplementacao] = useState(null);

    // --- Status Types State/Handlers ---
    const [statusTypes, setStatusTypes] = useState([]);
    const [isStatusTypeModalOpen, setIsStatusTypeModalOpen] = useState(false);
    const [selectedStatusType, setSelectedStatusType] = useState(null);

    const { toast } = useToast();
    const { user: currentUser } = useAuth();

    const fetchEmpresas = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/empresas`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao buscar empresas');
        setEmpresas(await response.json());
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleCreateEmpresa = () => {
      setSelectedEmpresa(null);
      setIsEmpresaModalOpen(true);
    };
    const handleEditEmpresa = (empresa) => {
      setSelectedEmpresa(empresa);
      setIsEmpresaModalOpen(true);
    };
    const onSaveEmpresa = async (formData) => {
      try {
        const token = localStorage.getItem('auth_token');
        const method = selectedEmpresa ? 'PUT' : 'POST';
        const url = selectedEmpresa
          ? `${API_BASE_URL}/empresas/${selectedEmpresa.id}`
          : `${API_BASE_URL}/empresas`;
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(formData),
        });
        if (!response.ok) throw new Error('Erro ao salvar empresa');
        const savedEmpresa = await response.json();
        if (selectedEmpresa) {
          setEmpresas(empresas.map(e => e.id === selectedEmpresa.id ? savedEmpresa : e));
          toast({ title: 'Sucesso', description: 'Empresa atualizada com sucesso.' });
        } else {
          setEmpresas([...empresas, savedEmpresa]);
          toast({ title: 'Sucesso', description: 'Empresa criada com sucesso.' });
        }
        setIsEmpresaModalOpen(false);
        setSelectedEmpresa(null);
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };
    const onDeleteEmpresa = async (empresa) => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/empresas/${empresa.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao excluir empresa');
        setEmpresas(empresas.filter(e => e.id !== empresa.id));
        toast({ title: 'Sucesso', description: 'Empresa excluída com sucesso.' });
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    // --- Metas Handlers ---
    const fetchMetas = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/metas`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao buscar metas');
        setMetas(await response.json());
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleCreateMeta = () => {
      setSelectedMeta(null);
      setIsMetaModalOpen(true);
    };

    const onSaveMeta = async (formData) => {
      try {
        const token = localStorage.getItem('auth_token');
        const method = selectedMeta ? 'PUT' : 'POST';
        const url = selectedMeta
          ? `${API_BASE_URL}/metas/${selectedMeta.id}`
          : `${API_BASE_URL}/metas`;
        const mappedData = {
          titulo: formData.name,
          descricao: formData.description,
          data_inicio: formData.openingDate,
          data_fim: formData.dueDate,
          status: formData.lifecycleStatus || 'Aberto',
          empresa_id: formData.empresa_id,
          progresso_atual: formData.current ?? formData.progresso_atual ?? 0,
          progresso_objetivo: formData.target ?? formData.progresso_objetivo ?? 100,
        };
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(mappedData),
        });
        if (!response.ok) throw new Error('Erro ao salvar meta');
        const savedMeta = await response.json();
        if (selectedMeta) {
          setMetas(metas.map(m => m.id === selectedMeta.id ? savedMeta : m));
          toast({ title: 'Sucesso', description: 'Meta atualizada com sucesso.' });
        } else {
          setMetas([...metas, savedMeta]);
          toast({ title: 'Sucesso', description: 'Meta criada com sucesso.' });
        }
        setIsMetaModalOpen(false);
        setSelectedMeta(null);
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleToggleMetaStatus = async (meta) => {
      const newStatus = meta.status === 'concluido' ? 'em_andamento' : 'concluido';
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/metas/${meta.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ ...meta, status: newStatus }),
        });
        if (!response.ok) throw new Error('Erro ao atualizar status da meta');
        const updatedMeta = await response.json();
        setMetas(metas.map(m => m.id === meta.id ? updatedMeta : m));
        toast({ title: 'Sucesso', description: `Meta ${newStatus === 'concluido' ? 'concluída' : 'reativada'}.` });
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    // --- Serviços Handlers ---
    const fetchServicos = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/servicos`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao buscar serviços');
        setServicos(await response.json());
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleCreateServico = () => {
      setSelectedServico(null);
      setIsServicoModalOpen(true);
    };

    const onSaveServico = async (formData) => {
      try {
        const token = localStorage.getItem('auth_token');
        const method = selectedServico ? 'PUT' : 'POST';
        const url = selectedServico
          ? `${API_BASE_URL}/servicos/${selectedServico.id}`
          : `${API_BASE_URL}/servicos`;
        const mappedData = {
          nome: formData.name,
          descricao: formData.description,
          empresa_id: formData.empresa_id,
          progresso_atual: formData.progresso_atual ?? 0,
          progresso_objetivo: formData.progresso_objetivo ?? 1,
        };
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(mappedData),
        });
        if (!response.ok) throw new Error('Erro ao salvar serviço');
        const savedServico = await response.json();
        if (selectedServico) {
          setServicos(servicos.map(s => s.id === selectedServico.id ? savedServico : s));
          toast({ title: 'Sucesso', description: 'Serviço atualizado com sucesso.' });
        } else {
          setServicos([...servicos, savedServico]);
          toast({ title: 'Sucesso', description: 'Serviço criado com sucesso.' });
        }
        setIsServicoModalOpen(false);
        setSelectedServico(null);
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    // --- Implementações Handlers ---
    const fetchAtendimentos = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/implementacoes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao buscar implementações');
        setAtendimentos(await response.json());
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleCreateAtendimento = () => {
      setSelectedAtendimento(null);
      setIsAtendimentoModalOpen(true);
    };

    const onSaveAtendimento = async (formData) => {
      try {
        const token = localStorage.getItem('auth_token');
        const method = selectedAtendimento ? 'PUT' : 'POST';
        const url = selectedAtendimento
          ? `${API_BASE_URL}/implementacoes/${selectedAtendimento.id}`
          : `${API_BASE_URL}/implementacoes`;
        const mappedData = {
          titulo: formData.title,
          descricao: formData.description,
          data_abertura: formData.openingDate,
          prazo: formData.dueDate,
          responsavel: formData.assignedTo,
          status: formData.status,
          empresa_id: formData.empresa_id,
        };
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(mappedData),
        });
        if (!response.ok) throw new Error('Erro ao salvar atendimento');
        const savedAtendimento = await response.json();
        if (selectedAtendimento) {
          setAtendimentos(atendimentos.map(a => a.id === selectedAtendimento.id ? savedAtendimento : a));
          toast({ title: 'Sucesso', description: 'Atendimento atualizado com sucesso.' });
        } else {
          setAtendimentos([...atendimentos, savedAtendimento]);
          toast({ title: 'Sucesso', description: 'Atendimento criado com sucesso.' });
        }
        setIsAtendimentoModalOpen(false);
        setSelectedAtendimento(null);
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    // --- Implementações Handlers ---
    const fetchImplementacoes = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/implementacoes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao buscar implementações');
        setImplementacoes(await response.json());
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    // --- Status Types Handlers ---
    const fetchStatusTypes = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/status-types`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao buscar tipos de status');
        setStatusTypes(await response.json());
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleCreateStatusType = () => {
      setSelectedStatusType(null);
      setIsStatusTypeModalOpen(true);
    };

    const onSaveStatusType = async (formData) => {
      try {
        const token = localStorage.getItem('auth_token');
        const method = selectedStatusType ? 'PUT' : 'POST';
        const url = selectedStatusType
          ? `${API_BASE_URL}/status-types/${selectedStatusType.id}`
          : `${API_BASE_URL}/status-types`;
        const mappedData = {
          name: formData.name,
          type: formData.type,
          empresa_id: formData.empresa_id,
        };
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(mappedData),
        });
        if (!response.ok) throw new Error('Erro ao salvar tipo de status');
        const savedStatusType = await response.json();
        if (selectedStatusType) {
          setStatusTypes(statusTypes.map(s => s.id === selectedStatusType.id ? savedStatusType : s));
          toast({ title: 'Sucesso', description: 'Tipo de status atualizado com sucesso.' });
        } else {
          setStatusTypes([...statusTypes, savedStatusType]);
          toast({ title: 'Sucesso', description: 'Tipo de status criado com sucesso.' });
        }
        setIsStatusTypeModalOpen(false);
        setSelectedStatusType(null);
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleDeleteStatusType = async (statusType) => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/status-types/${statusType.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Erro ao excluir tipo de status');
        setStatusTypes(statusTypes.filter(s => s.id !== statusType.id));
        toast({ title: 'Sucesso', description: 'Tipo de status excluído com sucesso.' });
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };

    const handleCreateImplementacao = () => {
      setSelectedImplementacao(null);
      setIsImplementacaoModalOpen(true);
    };

    const onSaveImplementacao = async (formData) => {
      try {
        const token = localStorage.getItem('auth_token');
        const method = selectedImplementacao ? 'PUT' : 'POST';
        const url = selectedImplementacao
          ? `${API_BASE_URL}/implementacoes/${selectedImplementacao.id}`
          : `${API_BASE_URL}/implementacoes`;
        const mappedData = {
          titulo: formData.name,
          descricao: formData.description,
          data_abertura: formData.openingDate,
          prazo: formData.dueDate,
          responsavel: formData.assignedTo,
          status: formData.status,
          progresso: formData.progress,
          checklist: formData.checklist,
          cliente: formData.client,
          empresa_id: formData.empresa_id,
        };
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(mappedData),
        });
        if (!response.ok) throw new Error('Erro ao salvar implementação');
        const savedImplementacao = await response.json();
        if (selectedImplementacao) {
          setImplementacoes(implementacoes.map(i => i.id === selectedImplementacao.id ? savedImplementacao : i));
          toast({ title: 'Sucesso', description: 'Implementação atualizada com sucesso.' });
        } else {
          setImplementacoes([...implementacoes, savedImplementacao]);
          toast({ title: 'Sucesso', description: 'Implementação criada com sucesso.' });
        }
        setIsImplementacaoModalOpen(false);
        setSelectedImplementacao(null);
      } catch (error) {
        toast({ title: 'Erro', description: error.message, variant: 'destructive' });
      }
    };
  // Redireciona usuário comum
  useEffect(() => {
    if (currentUser && currentUser.role === 'usuario') {
      window.location.href = '/dashboard';
    }
  }, [currentUser]);

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'empresas' && currentUser?.role === 'superadmin') {
      fetchEmpresas();
    }
  }, [activeTab, currentUser]);

  useEffect(() => {
    if (activeTab === 'goals') {
      fetchMetas();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'services') {
      fetchServicos();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAtendimentos();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'implementations') {
      fetchImplementacoes();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'settings') {
      fetchStatusTypes();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Erro ao buscar usuários');
      }
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsUserModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const onSaveUser = async (formData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const method = selectedUser ? 'PUT' : 'POST';
      const url = selectedUser 
        ? `${API_BASE_URL}/admin/users/${selectedUser.id}`
        : `${API_BASE_URL}/admin/users`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao salvar usuário');
      }

      const savedUser = await response.json();
      
      if (selectedUser) {
        setUsers(users.map(u => u.id === selectedUser.id ? savedUser : u));
        toast({ title: "Sucesso", description: "Usuário atualizado com sucesso." });
      } else {
        setUsers([...users, savedUser]);
        toast({ title: "Sucesso", description: "Usuário criado com sucesso." });
      }

      setIsUserModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const onDeleteUser = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao excluir usuário');
      }

      setUsers(users.filter(u => u.id !== selectedUser.id));
      toast({ title: "Sucesso", description: "Usuário excluído com sucesso." });
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin - VHSYS</title>
        <meta name="description" content="Painel de administração" />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-2">
                  <ShieldCheck className="w-8 h-8 text-orange-500" />
                  Painel Super Admin
                </h1>
                <p className="text-gray-400">Gerencie usuários, metas e configurações</p>
              </div>
            </div>
          </div>

          <TabsRoot value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-7 bg-gray-800">
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Usuários
              </TabsTrigger>
              <TabsTrigger value="goals" className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Metas
              </TabsTrigger>
              <TabsTrigger value="services" className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Serviços
              </TabsTrigger>
              <TabsTrigger value="attendance" className="flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                Implementações
              </TabsTrigger>
              <TabsTrigger value="implementations" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Implementações
              </TabsTrigger>
              <TabsTrigger value="empresas" className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                Empresas
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configurações
              </TabsTrigger>
            </TabsList>

            <TabsContent value="empresas" className="mt-6">
              {currentUser?.role !== 'superadmin' ? (
                <div className="text-center py-8 text-gray-400">Acesso restrito ao superadmin.</div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <Building className="w-6 h-6 text-orange-500" />
                      <h2 className="text-xl font-semibold">Empresas</h2>
                    </div>
                    <Button className="bg-orange-600 hover:bg-orange-700" onClick={handleCreateEmpresa}>
                      <Plus className="w-4 h-4 mr-2" />Nova Empresa
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4">Nome</th>
                          <th className="text-left py-3 px-4">CNPJ</th>
                          <th className="text-left py-3 px-4">Email</th>
                          <th className="text-left py-3 px-4">Telefone</th>
                          <th className="text-left py-3 px-4">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empresas.map((empresa) => (
                          <tr key={empresa.id} className="border-b border-gray-200 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50">
                            <td className="py-3 px-4">{empresa.nome}</td>
                            <td className="py-3 px-4">{empresa.cnpj || '-'}</td>
                            <td className="py-3 px-4">{empresa.email || '-'}</td>
                            <td className="py-3 px-4">{empresa.telefone || '-'}</td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleEditEmpresa(empresa)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300" onClick={() => onDeleteEmpresa(empresa)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </TabsContent>

            <TabsContent value="users" className="mt-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Users className="w-6 h-6 text-blue-500" />
                    <h2 className="text-xl font-semibold">Usuários do Sistema</h2>
                  </div>
                  {(currentUser?.role === 'superadmin' || currentUser?.role === 'admin') && (
                    <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateUser}>
                      <Plus className="w-4 h-4 mr-2" />
                      Novo Usuário
                    </Button>
                  )}
                </div>

                {loading ? (
                  <div className="text-center py-8">Carregando...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4">Nome</th>
                          <th className="text-left py-3 px-4">Email</th>
                          <th className="text-left py-3 px-4">Role</th>
                          <th className="text-left py-3 px-4">Empresa</th>
                          <th className="text-left py-3 px-4">Criado em</th>
                          <th className="text-left py-3 px-4">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id} className="border-b border-gray-200 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50">
                            <td className="py-3 px-4">{user.name}</td>
                            <td className="py-3 px-4">{user.email}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs ${
                                user.role === 'superadmin' ? 'bg-red-500/20 text-red-400' :
                                user.role === 'admin' ? 'bg-orange-500/20 text-orange-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                {user.role}
                              </span>
                            </td>
                            <td className="py-3 px-4 flex items-center gap-2">
                              <Building className="w-4 h-4 text-gray-400" />
                              {user.empresa || 'N/A'}
                            </td>
                            <td className="py-3 px-4">
                              {new Date(user.created_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                {(currentUser?.role === 'superadmin' || (currentUser?.role === 'admin' && user.empresa === currentUser.empresa)) && (
                                  <>
                                    <Button size="sm" variant="outline" onClick={() => handleEditUser(user)}>
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteUser(user)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            </TabsContent>

            <TabsContent value="goals" className="mt-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Target className="w-6 h-6 text-green-500" />
                    <h2 className="text-xl font-semibold">Metas</h2>
                  </div>
                  <Button className="bg-green-600 hover:bg-green-700" onClick={handleCreateMeta}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Meta
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4"></th>
                        <th className="text-left py-3 px-4">Título</th>
                        <th className="text-left py-3 px-4">Descrição</th>
                        <th className="text-left py-3 px-4">Data Início</th>
                        <th className="text-left py-3 px-4">Data Fim</th>
                        <th className="text-left py-3 px-4">Empresa</th>
                        <th className="text-left py-3 px-4">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metas.map((meta) => (
                        <tr key={meta.id} className="border-b border-gray-200 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50">
                          <td className="py-3 px-4">
                            <Button variant="ghost" size="sm" onClick={() => handleToggleMetaStatus(meta)}>
                              {meta.status === 'concluido' ? <Check className="w-4 h-4 text-green-500" /> : <div className="w-4 h-4 border border-gray-500 rounded"></div>}
                            </Button>
                          </td>
                          <td className="py-3 px-4">{meta.titulo}</td>
                          <td className="py-3 px-4">{meta.descricao || '-'}</td>
                          <td className="py-3 px-4">{meta.data_inicio ? new Date(meta.data_inicio).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="py-3 px-4">{meta.data_fim ? new Date(meta.data_fim).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="py-3 px-4">{meta.empresa?.nome || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedMeta(meta); setIsMetaModalOpen(true); }}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="services" className="mt-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-6 h-6 text-purple-500" />
                    <h2 className="text-xl font-semibold">Serviços</h2>
                  </div>
                  <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleCreateServico}>
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Serviço
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4">Nome</th>
                        <th className="text-left py-3 px-4">Descrição</th>
                        <th className="text-left py-3 px-4">Empresa</th>
                        <th className="text-left py-3 px-4">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servicos.map((servico) => (
                        <tr key={servico.id} className="border-b border-gray-200 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50">
                          <td className="py-3 px-4">{servico.nome}</td>
                          <td className="py-3 px-4">{servico.descricao || '-'}</td>
                          <td className="py-3 px-4">{servico.empresa?.nome || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedServico(servico); setIsServicoModalOpen(true); }}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="attendance" className="mt-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-6 h-6 text-yellow-500" />
                    <h2 className="text-xl font-semibold">Implementações</h2>
                  </div>
                  <Button className="bg-yellow-600 hover:bg-yellow-700" onClick={handleCreateAtendimento}>
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Atendimento
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4">Título</th>
                        <th className="text-left py-3 px-4">Descrição</th>
                        <th className="text-left py-3 px-4">Data Abertura</th>
                        <th className="text-left py-3 px-4">Prazo</th>
                        <th className="text-left py-3 px-4">Responsável</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Empresa</th>
                        <th className="text-left py-3 px-4">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {atendimentos.map((atendimento) => (
                        <tr key={atendimento.id} className="border-b border-gray-200 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50">
                          <td className="py-3 px-4">{atendimento.titulo}</td>
                          <td className="py-3 px-4">{atendimento.descricao || '-'}</td>
                          <td className="py-3 px-4">{atendimento.data_abertura ? new Date(atendimento.data_abertura).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="py-3 px-4">{atendimento.prazo ? new Date(atendimento.prazo).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="py-3 px-4">{atendimento.responsavel || '-'}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              atendimento.status === 'concluido' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {atendimento.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">{atendimento.empresa?.nome || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedAtendimento(atendimento); setIsAtendimentoModalOpen(true); }}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="implementations" className="mt-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Settings className="w-6 h-6 text-blue-500" />
                    <h2 className="text-xl font-semibold">Implementações</h2>
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateImplementacao}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Implementação
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4">Título</th>
                        <th className="text-left py-3 px-4">Descrição</th>
                        <th className="text-left py-3 px-4">Data Abertura</th>
                        <th className="text-left py-3 px-4">Prazo</th>
                        <th className="text-left py-3 px-4">Responsável</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Progresso</th>
                        <th className="text-left py-3 px-4">Cliente</th>
                        <th className="text-left py-3 px-4">Empresa</th>
                        <th className="text-left py-3 px-4">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {implementacoes.map((implementacao) => (
                        <tr key={implementacao.id} className="border-b border-gray-200 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50">
                          <td className="py-3 px-4">{implementacao.titulo}</td>
                          <td className="py-3 px-4">{implementacao.descricao || '-'}</td>
                          <td className="py-3 px-4">{implementacao.data_abertura ? new Date(implementacao.data_abertura).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="py-3 px-4">{implementacao.prazo ? new Date(implementacao.prazo).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="py-3 px-4">{implementacao.responsavel || '-'}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              implementacao.status === 'concluido' ? 'bg-green-500/20 text-green-400' :
                              implementacao.status === 'em_andamento' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {implementacao.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">{implementacao.progresso}%</td>
                          <td className="py-3 px-4">{implementacao.cliente || '-'}</td>
                          <td className="py-3 px-4">{implementacao.empresa?.nome || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedImplementacao(implementacao); setIsImplementacaoModalOpen(true); }}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="settings" className="mt-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Settings className="w-6 h-6 text-gray-500" />
                    <h2 className="text-xl font-semibold">Tipos de Status</h2>
                  </div>
                  <Button className="bg-gray-600 hover:bg-gray-700" onClick={handleCreateStatusType}>
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Tipo de Status
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4">Nome</th>
                        <th className="text-left py-3 px-4">Tipo</th>
                        <th className="text-left py-3 px-4">Empresa</th>
                        <th className="text-left py-3 px-4">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusTypes.map((statusType) => (
                        <tr key={statusType.id} className="border-b border-gray-200 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50">
                          <td className="py-3 px-4">{statusType.name}</td>
                          <td className="py-3 px-4">{statusType.type}</td>
                          <td className="py-3 px-4">{statusType.empresa?.nome || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedStatusType(statusType); setIsStatusTypeModalOpen(true); }}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteStatusType(statusType)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </TabsContent>
          </TabsRoot>
        </div>
      </div>

      <UserManagementModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setSelectedUser(null);
        }}
        onSave={onSaveUser}
        user={selectedUser}
      />

      <CreateEditModal
        isOpen={isEmpresaModalOpen}
        onClose={() => {
          setIsEmpresaModalOpen(false);
          setSelectedEmpresa(null);
        }}
        onSave={onSaveEmpresa}
        item={selectedEmpresa}
        type="empresa"
      />

      <CreateEditModal
        isOpen={isMetaModalOpen}
        onClose={() => {
          setIsMetaModalOpen(false);
          setSelectedMeta(null);
        }}
        onSave={onSaveMeta}
        item={selectedMeta}
        type="goals"
      />

      <CreateEditModal
        isOpen={isServicoModalOpen}
        onClose={() => {
          setIsServicoModalOpen(false);
          setSelectedServico(null);
        }}
        onSave={onSaveServico}
        item={selectedServico}
        type="services"
      />

      <CreateEditModal
        isOpen={isAtendimentoModalOpen}
        onClose={() => {
          setIsAtendimentoModalOpen(false);
          setSelectedAtendimento(null);
        }}
        onSave={onSaveAtendimento}
        item={selectedAtendimento}
        type="attendance"
      />

      <CreateEditModal
        isOpen={isImplementacaoModalOpen}
        onClose={() => {
          setIsImplementacaoModalOpen(false);
          setSelectedImplementacao(null);
        }}
        onSave={onSaveImplementacao}
        item={selectedImplementacao}
        type="implementations"
      />

      <CreateEditModal
        isOpen={isStatusTypeModalOpen}
        onClose={() => {
          setIsStatusTypeModalOpen(false);
          setSelectedStatusType(null);
        }}
        onSave={onSaveStatusType}
        item={selectedStatusType}
        type="statusType"
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={onDeleteUser}
        title="Excluir Usuário"
        description={`Tem certeza que deseja excluir o usuário "${selectedUser?.name}"? Esta ação não pode ser desfeita.`}
      />
    </>
  );
};

export default AdminDashboard;